import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  activityPath,
  ACTIVITY_LOCK_REASON,
  detectActivityFromPath,
  PROJECT_ACTIVITIES,
  type ActivityId,
} from './projectActivities';
import { ProjectLifecycle } from './useProjectLifecycle';

const ACTIVITY_KICKER: Record<ActivityId, string> = {
  discover: 'Explore',
  understand: 'Analyze',
  operate: 'Execute',
};

/**
 * Phase mascots — only the current phase animates: .m-head wobbles,
 * .m-code pulses, .m-spark fires, .m-cursor blinks, .m-ping blinks.
 */
const MASCOT: Record<ActivityId, React.ReactNode> = {
  discover: (
    <>
      <g className="m-head">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m15.3 15.3 5 5" />
        <path className="m-code" d="M9 8.3 6.9 10.5 9 12.7M12 8.3l2.1 2.2-2.1 2.2" />
      </g>
      <path className="m-ping" d="M19.5 3v3M18 4.5h3" />
    </>
  ),
  understand: (
    <g className="m-head">
      <path d="M9.5 4a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 2 5 3 3 0 0 0 3 3A2.5 2.5 0 0 0 12 17.5v-11A2.5 2.5 0 0 0 9.5 4Z" />
      <path d="M14.5 4a3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1-2 5 3 3 0 0 1-3 3 2.5 2.5 0 0 1-2.5-2.5v-11A2.5 2.5 0 0 1 14.5 4Z" />
      <circle className="m-spark" cx="8.5" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle className="m-spark s2" cx="15.5" cy="9" r="1" fill="currentColor" stroke="none" />
      <circle className="m-spark s3" cx="15" cy="15" r="1" fill="currentColor" stroke="none" />
    </g>
  ),
  operate: (
    <g className="m-head">
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="m7 10 2.5 2.5L7 15" />
      <path className="m-cursor" d="M12 15h4.5" />
    </g>
  ),
};

const Svg: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

interface ProjectActivityRailProps {
  projectId: string;
  lifecycle: ProjectLifecycle;
}

/** Discover → Understand → Operate as three phase cards. */
const ProjectActivityRail: React.FC<ProjectActivityRailProps> = ({ projectId, lifecycle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeActivity = detectActivityFromPath(location.pathname);

  const isDone: Record<ActivityId, boolean> = {
    discover: lifecycle.phase0HandoffAccepted,
    understand: lifecycle.understandingCompleted,
    operate: false,
  };
  const lockedOf = (id: ActivityId, phaseId: (typeof PROJECT_ACTIVITIES)[number]['phaseId']) =>
    id !== 'operate' && lifecycle.phaseAccess[phaseId] === 'locked';

  // Transient animation flags: icon pop on the newly active phase, shake on a
  // locked click, and unlock / completion flourishes when lifecycle advances.
  const [entering, setEntering] = useState<ActivityId | null>(null);
  const [nope, setNope] = useState<ActivityId | null>(null);
  const [justUnlocked, setJustUnlocked] = useState<Set<ActivityId>>(new Set());
  const [justDone, setJustDone] = useState<Set<ActivityId>>(new Set());

  const prevActive = useRef(activeActivity);
  useEffect(() => {
    if (prevActive.current === activeActivity) return undefined;
    prevActive.current = activeActivity;
    if (!activeActivity) return undefined;
    setEntering(activeActivity);
    const t = window.setTimeout(() => setEntering(null), 700);
    return () => window.clearTimeout(t);
  }, [activeActivity]);

  const lockedKey = PROJECT_ACTIVITIES.map((a) => (lockedOf(a.id, a.phaseId) ? 1 : 0)).join('');
  const doneKey = PROJECT_ACTIVITIES.map((a) => (isDone[a.id] ? 1 : 0)).join('');
  const prevLockedKey = useRef(lockedKey);
  const prevDoneKey = useRef(doneKey);
  useEffect(() => {
    const unlocked = new Set<ActivityId>();
    const completed = new Set<ActivityId>();
    PROJECT_ACTIVITIES.forEach((a, i) => {
      if (prevLockedKey.current[i] === '1' && lockedKey[i] === '0') unlocked.add(a.id);
      if (prevDoneKey.current[i] === '0' && doneKey[i] === '1') completed.add(a.id);
    });
    prevLockedKey.current = lockedKey;
    prevDoneKey.current = doneKey;
    if (!unlocked.size && !completed.size) return undefined;
    setJustUnlocked(unlocked);
    setJustDone(completed);
    const t = window.setTimeout(() => {
      setJustUnlocked(new Set());
      setJustDone(new Set());
    }, 1200);
    return () => window.clearTimeout(t);
  }, [lockedKey, doneKey]);

  // 3D tilt + glare follow the pointer on unlocked cards (fine pointers only).
  const canTilt =
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches &&
    !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const onTiltMove = (e: React.PointerEvent<HTMLOListElement>) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('.wf-step-btn');
    if (!b || b.closest('.locked')) return;
    const r = b.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    b.classList.add('tilting');
    b.style.setProperty('--rx', `${((0.5 - y) * 10).toFixed(2)}deg`);
    b.style.setProperty('--ry', `${((x - 0.5) * 12).toFixed(2)}deg`);
    b.style.setProperty('--gx', `${x * 100}%`);
    b.style.setProperty('--gy', `${y * 100}%`);
  };
  const onTiltOut = (e: React.PointerEvent<HTMLOListElement>) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('.wf-step-btn');
    if (!b || b.contains(e.relatedTarget as Node | null)) return;
    b.classList.remove('tilting');
    b.style.removeProperty('--rx');
    b.style.removeProperty('--ry');
  };

  return (
    <ol
      className="wf-steps"
      aria-label="Project phases"
      onPointerMove={canTilt ? onTiltMove : undefined}
      onPointerOut={canTilt ? onTiltOut : undefined}
    >
      {PROJECT_ACTIVITIES.map((activity, i) => {
        const locked = lockedOf(activity.id, activity.phaseId);
        const isActive = activeActivity === activity.id;
        const done = !locked && !isActive && isDone[activity.id];
        const lockReason = ACTIVITY_LOCK_REASON[activity.id];
        const cls = [
          'wf-step',
          isActive && 'active',
          locked && 'locked',
          done && 'done',
          isActive && entering === activity.id && 'entering',
          nope === activity.id && 'nope',
          justUnlocked.has(activity.id) && 'just-unlocked',
          justDone.has(activity.id) && 'just-done',
        ]
          .filter(Boolean)
          .join(' ');

        const state = locked ? (
          <span className="wf-state">
            <Svg>
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </Svg>
            Locked
          </span>
        ) : isActive ? (
          <span className="wf-state">
            <i />
            Current
          </span>
        ) : done ? (
          <span className="wf-state">
            <Svg>
              <path d="m5 12.5 4.5 4.5L19 7.5" />
            </Svg>
            Complete
          </span>
        ) : (
          <span className="wf-state">Open</span>
        );

        const node = (
          <li
            className={cls}
            data-phase={activity.id}
            style={{ '--i': i } as React.CSSProperties}
            onAnimationEnd={(e) => {
              if (nope === activity.id && e.target instanceof HTMLElement && e.target.classList.contains('wf-step-btn')) {
                setNope(null);
              }
            }}
          >
            <button
              type="button"
              className="wf-step-btn"
              // aria-disabled (not disabled) keeps a locked card focusable so the
              // tooltip explaining the lock is reachable by keyboard too.
              aria-disabled={locked || undefined}
              aria-current={isActive ? 'step' : undefined}
              aria-label={
                locked
                  ? `${activity.label} (locked) — ${lockReason}`
                  : `${activity.label}: ${activity.description}`
              }
              onClick={() => {
                if (locked) {
                  // Restart the shake even on repeated clicks.
                  setNope(null);
                  window.requestAnimationFrame(() => setNope(activity.id));
                  return;
                }
                navigate(activityPath(projectId, activity.id));
              }}
            >
              <span className="glare" aria-hidden="true" />
              <span className="wf-step-ico">
                <span className="orbit" aria-hidden="true" />
                <Svg>{MASCOT[activity.id]}</Svg>
              </span>
              <span className="wf-step-txt">
                <span className="wf-step-k">{ACTIVITY_KICKER[activity.id]}</span>
                <span className="wf-step-n">{activity.label}</span>
                {locked && <span className="wf-step-d">{lockReason}</span>}
              </span>
              {state}
            </button>
          </li>
        );

        return locked ? (
          <Tooltip key={activity.id} title={lockReason} arrow placement="bottom">
            {node}
          </Tooltip>
        ) : (
          <React.Fragment key={activity.id}>{node}</React.Fragment>
        );
      })}
    </ol>
  );
};

export default ProjectActivityRail;
